import { ChevronDown, UserCircle2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { TopBarProfileMenuPanel } from './top-bar-profile-menu-panel';
import type { TopBarCaseMenuActions } from './top-bar-menu-context';

interface TopBarProfileMenuProps {
  readonly triggerLabel: string;
  readonly userEmail: string | null;
  readonly showAdminLink: boolean;
  readonly caseMenuActions: TopBarCaseMenuActions | null;
  readonly onSignOut: () => void;
}

export function TopBarProfileMenu({
  triggerLabel,
  userEmail,
  showAdminLink,
  caseMenuActions,
  onSignOut,
}: TopBarProfileMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    function onPointerDown(event: MouseEvent) {
      if (!menuRef.current) return;
      if (menuRef.current.contains(event.target as Node)) return;
      setIsOpen(false);
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false);
    }

    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  return (
    <div className="ml-auto" ref={menuRef}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs text-text-secondary transition-colors hover:bg-white/6 hover:text-text-primary"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label="Åpne konto- og profilmeny"
      >
        <UserCircle2 size={14} />
        <span>{triggerLabel}</span>
        <ChevronDown size={14} />
      </button>

      {isOpen && (
        <TopBarProfileMenuPanel
          userEmail={userEmail}
          showAdminLink={showAdminLink}
          caseMenuActions={caseMenuActions}
          onClose={() => setIsOpen(false)}
          onSignOut={onSignOut}
        />
      )}
    </div>
  );
}
